import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Assessment as AnalyticsIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Business as InfrastructureIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  getComprehensiveAnalytics,
  getIndicatorsByCategory,
  getUniqueAreas,
  getUniqueCategories,
  getCategoryTotals,
  exportComprehensiveAnalytics,
  formatNumber,
  getCategoryColor,
  type ComprehensiveAnalyticsData,
  type IndicatorResult,
} from '../../../services/comprehensiveAnalyticsApi';

const GROUPING_OPTIONS = [
  { value: 'district', label: 'By District' },
  { value: 'region', label: 'By Region' },
  { value: 'national', label: 'National Total' },
];

const ACADEMIC_YEARS = [
  { value: '2026', label: '2025/2026' },
  { value: '2025', label: '2024/2025' },
  { value: '2024', label: '2023/2024' },
  { value: '2023', label: '2022/2023' },
];

const ComprehensiveAnalytics: React.FC = () => {
  const [groupBy, setGroupBy] = useState<string>('district');
  const [academicYear, setAcademicYear] = useState<string>('2026');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');

  // Fetch comprehensive analytics data
  const { data: analyticsData, isLoading, error, refetch } = useQuery({
    queryKey: ['comprehensive-analytics', groupBy, academicYear],
    queryFn: () => getComprehensiveAnalytics(groupBy, academicYear),
  });

  const data = analyticsData?.data;
  const categories = data ? getUniqueCategories(data) : [];
  const areas = data ? getUniqueAreas(data) : [];
  const categoryTotals = data ? getCategoryTotals(data) : {};

  // Filter indicators based on selections
  const getFilteredIndicators = (): IndicatorResult[] => {
    if (!data) return [];
    
    let indicators = [
      ...data.moe_indicators,
      ...data.infrastructure,
      ...data.enrollment,
      ...data.staff,
    ];

    if (selectedCategory) {
      indicators = indicators.filter(indicator => 
        indicator.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    if (selectedArea) {
      indicators = indicators.filter(indicator => 
        indicator.area_name.toLowerCase() === selectedArea.toLowerCase()
      );
    }

    return indicators;
  };

  const filteredIndicators = getFilteredIndicators();

  const handleExport = () => {
    if (!data) return;
    try {
      exportComprehensiveAnalytics(data);
    } catch (error: any) {
      alert(`Export failed: ${error.message}`);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'moe standard':
        return <AnalyticsIcon />;
      case 'infrastructure':
        return <InfrastructureIcon />;
      case 'enrollment':
        return <PeopleIcon />;
      case 'staff':
        return <SchoolIcon />;
      default:
        return <AnalyticsIcon />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading comprehensive analytics...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load comprehensive analytics: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info">
        No analytics data available.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Comprehensive Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Overview of all indicators across the education system
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => refetch()} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export All Data">
            <IconButton onClick={handleExport}>
              <ExportIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Analysis Parameters
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                label="Academic Year"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
              >
                {ACADEMIC_YEARS.map(year => (
                  <MenuItem key={year.value} value={year.value}>
                    {year.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                label="Grouping Level"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                {GROUPING_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                label="Filter by Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                label="Filter by Area"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
                disabled={groupBy === 'national'}
              >
                <MenuItem value="">All Areas</MenuItem>
                {areas.map(area => (
                  <MenuItem key={area} value={area}>
                    {area}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Total Indicators
              </Typography>
              <Typography variant="h4">
                {data.summary.total_indicators}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary">
                Areas Covered
              </Typography>
              <Typography variant="h4">
                {data.summary.total_areas}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                Categories
              </Typography>
              <Typography variant="h4">
                {categories.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                Filtered Results
              </Typography>
              <Typography variant="h4">
                {filteredIndicators.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Category Overview */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Category Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {Object.entries(categoryTotals).map(([category, totals]) => (
          <Grid item xs={12} sm={6} md={4} key={category}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getCategoryIcon(category)}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {category}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {totals.count} indicators
                </Typography>
                <Typography variant="h5" color={getCategoryColor(category)}>
                  {formatNumber(totals.total)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detailed Results */}
      <Typography variant="h5" gutterBottom>
        Detailed Results
      </Typography>
      
      {/* Group by category */}
      {categories.map(category => {
        const categoryIndicators = getIndicatorsByCategory(data, category);
        const filteredCategoryIndicators = categoryIndicators.filter(indicator => {
          if (selectedArea) {
            return indicator.area_name.toLowerCase() === selectedArea.toLowerCase();
          }
          return true;
        });

        if (filteredCategoryIndicators.length === 0 && selectedCategory && selectedCategory !== category) {
          return null;
        }

        return (
          <Accordion key={category} defaultExpanded={categories.length <= 2}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {getCategoryIcon(category)}
                <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                  {category} ({filteredCategoryIndicators.length} indicators)
                </Typography>
                <Chip 
                  label={formatNumber(filteredCategoryIndicators.reduce((sum, ind) => sum + ind.indicator_value, 0))}
                  color="primary"
                  size="small"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Indicator</strong></TableCell>
                      <TableCell><strong>Area</strong></TableCell>
                      <TableCell align="right"><strong>Value</strong></TableCell>
                      <TableCell><strong>Source</strong></TableCell>
                      <TableCell><strong>Period</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCategoryIndicators.map((indicator, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {indicator.indicator_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {indicator.indicator_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{indicator.area_name}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight="bold">
                            {indicator.indicator_value.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={indicator.source} size="small" />
                        </TableCell>
                        <TableCell>{indicator.period}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default ComprehensiveAnalytics;